import cx from 'classnames';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { colors } from '~/theme.css';
import { isBun } from '~/utils/constants';
import * as styles from './Title.css';

interface TitleProps
  extends React.DetailedHTMLProps<React.HTMLProps<HTMLHeadingElement>, HTMLHeadingElement>, React.AriaAttributes {}

interface EditableTitleProps extends TitleProps {
  onRename: (newName: string) => void;
}

const joinJSXAndStrings = (array: ReactNode[]) =>
  array.reduce<ReactNode[]>((acc, curr) => {
    if (typeof acc[acc.length - 1] === 'string' && typeof curr === 'string') {
      (acc[acc.length - 1] as string) += curr;
    } else {
      acc.push(curr);
    }
    return acc;
  }, []);

/**
 * Formats specific letter combinations in a title
 * Single letter formatting is performed in index.css
 * @returns
 */
const formatTitle = (title: string) => {
  const arr: ReactNode[] = title.split('').map((item, i, arr: ReactNode[]) => {
    // Format "fi"
    if (item === 'f' && arr[i + 1] === 'i') {
      return (
        <span
          key={`${item}-${i}`}
          style={{
            letterSpacing: 0,
          }}
        >
          {item}
        </span>
      );
    }
    // Format "LA"
    if (item === 'L' && arr[i + 1] === 'A') {
      return (
        <span
          key={`${item}-${i}`}
          style={{
            letterSpacing: 0.5,
          }}
        >
          {item}
        </span>
      );
    }
    // Format "rt"
    if (item === 'r' && arr[i + 1] === 't') {
      return (
        <span
          key={`${item}-${i}`}
          style={{
            letterSpacing: 0,
          }}
        >
          {item}
        </span>
      );
    }
    return item;
  });

  return joinJSXAndStrings(arr);
};

const shouldFormat = (children: ReactNode, format = !isBun): children is string => {
  if (!format) return false;
  if (typeof children !== 'string' || !children) return false;
  const result = children.includes('fi') || children.includes('LA') || children.includes('rt');
  return result;
};

/**
 * @param props
 * @property {boolean} [format] - formatting is disabled by default for tests, allow overriding that
 */
const Title: React.FC<TitleProps & { format?: boolean }> = (props) => {
  const { children, className, format, ...rest } = props;
  const result = shouldFormat(children, format) ? formatTitle(children) : children;

  return (
    <h1 className={className ?? styles.title} {...rest}>
      {result}
    </h1>
  );
};

type UnstyledTitleProps = {
  children: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
} & Omit<React.HTMLAttributes<HTMLHeadingElement>, 'children'>;

export const UnstyledTitle: React.FC<UnstyledTitleProps> = ({ children, as: Component = 'h1', style, ...rest }) => {
  const result = shouldFormat(children) ? formatTitle(children) : children;
  return (
    <Component style={{ fontFamily: 'var(--font-title)', color: colors.text.primary, ...style }} {...rest}>
      {result}
    </Component>
  );
};

export const EditableTitle: React.FC<EditableTitleProps> = (props) => {
  const { children, className, onRename, ...rest } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDoubleClick = () => {
    const textContent = typeof children === 'string' ? children : '';
    setEditValue(textContent);
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== children) {
      onRename(trimmedValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const result = shouldFormat(children) ? formatTitle(children) : children;

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={cx(className ?? styles.title, styles.input)}
      />
    );
  }

  return (
    <h1 className={cx(className ?? styles.title, styles.editable)} onDoubleClick={handleDoubleClick} {...rest}>
      {result}
    </h1>
  );
};

export default Title;
